import { parseAN3 } from './an3.mjs';
import { parseCharacterConfig } from './character-config.mjs';

// Read only the selected character's authored resources. Multiple actors may
// share clip basenames, so directory identity matters when loading a party.
export async function loadActorAssets(name, files, { player = false } = {}) {
  name = String(name).toLowerCase();
  if (!/^[a-z0-9_-]{1,96}$/.test(name)) throw new Error('Invalid character identifier.');
  const list = [...files], normalized = file => (file.webkitRelativePath || file.name).replaceAll('\\', '/').toLowerCase();
  const configs = list.filter(file => file.name.toLowerCase() === `${name}.txt`);
  if (configs.length !== 1) throw new Error(`Folder needs one original ${name}.TXT.`);
  const directory = normalized(configs[0]).replace(/[^/]+$/, '');
  const own = list.filter(file => normalized(file).replace(/[^/]+$/, '') === directory);
  // Original variants such as BattleDroid_Security inherit the base TXT in
  // their authored character directory. They inherit its TXT and clips; the
  // variant file overrides identity/material-layer settings after inheritance.
  const visited = new Set(), chain = [];
  let current = name;
  while (true) {
    if (visited.has(current) || visited.size >= 16) throw new Error('Cyclic or excessive original character TXT inheritance.');
    visited.add(current);
    const txt = own.filter(file => file.name.toLowerCase() === `${current}.txt`);
    if (txt.length !== 1) throw new Error(`Missing/ambiguous original inherited character ${current}.TXT.`);
    const parsed = parseCharacterConfig(await txt[0].text()); chain.unshift(parsed);
    if (parsed.values.txt_file === undefined) break;
    current = String(parsed.values.txt_file).toLowerCase();
    if (!/^[a-z0-9_-]{1,96}$/.test(current)) throw new Error('Invalid original character TXT inheritance reference.');
  }
  const config = { values: Object.create(null), layers: Object.create(null), animations: new Map(), actions: new Map() };
  for (const parsed of chain) {
    Object.assign(config.values, parsed.values); Object.assign(config.layers, parsed.layers);
    for (const [clip, animation] of parsed.animations) config.animations.set(clip, animation);
  }
  for (const animation of config.animations.values()) if (animation.action !== null) config.actions.set(animation.action, animation);
  // GCDATA_DEFAULT offsets0x24/0x2c/0x38. Protocol characters omit jump
  // parameters because Move_DROIDGENERIC passes zero jump input; gravity
  // still applies. Preserve original defaults rather than rejecting TC14.
  if (player) for (const [key, value] of Object.entries({ air_gravity: -5, jump_speed: 2, jump_2_speed: 2 }))
    if (config.values[key] === undefined) config.values[key] = value;
  const ownModel = own.filter(file => file.name.toLowerCase() === `${name}_pc.ghg`);
  const candidates = ownModel.length ? ownModel : own.filter(file => file.name.toLowerCase() === `${current}_pc.ghg`);
  if (candidates.length !== 1) throw new Error(`Folder needs one original ${current}_PC.GHG.`);
  const ghg = candidates[0], animations = new Map(), animationSources = new Map();
  const required = ['radius', 'miny', 'maxy', 'hit_points', 'acceleration', 'walk_speed', 'run_speed'];
  if (player) required.push('jump_speed', 'jump_2_speed', 'air_gravity');
  if (required.some(key => !Number.isFinite(config.values[key])) || config.values.radius <= 0 || config.values.maxy <= config.values.miny || config.values.hit_points <= 0)
    throw new Error(`Invalid original ${name} movement/collision configuration.`);
  for (const file of own.filter(file => /\.an3$/i.test(file.name))) {
    const clip = file.name.slice(0, -4).toLowerCase();
    if (animations.has(clip)) throw new Error(`Duplicate ${name} animation ${clip}.`);
    animations.set(clip, parseAN3(await file.arrayBuffer()));
    animationSources.set(clip, normalized(file));
  }
  // APIObject GetAnimationPath/RedirectAnim supports CHARS/COMMONANIMS for
  // named shared animations. Original ObiWan explicitly authors e.g.
  // splat_old_jedichar and ride_*_old_jedichar there. Resolve only missing
  // declared names from that exact shared directory; never borrow IDLE or
  // another character's same-named clip through an unrestricted search.
  const common = list.filter(file => /(?:^|\/)chars\/commonanims\/[^/]+\.an3$/.test(normalized(file)));
  for (const animation of config.animations.values()) if (!animations.has(animation.clip)) {
    const shared = common.filter(file => file.name.toLowerCase() === `${animation.clip}.an3`);
    if (shared.length > 1) throw new Error(`Ambiguous original common animation ${animation.clip}.AN3.`);
    if (shared.length === 1) {
      animations.set(animation.clip, parseAN3(await shared[0].arrayBuffer()));
      animationSources.set(animation.clip, normalized(shared[0]));
    }
  }
  for (const action of config.actions.values()) if (!animations.has(action.clip)) throw new Error(`Missing ${name} animation ${action.clip}.AN3.`);
  if (!animations.has('idle')) throw new Error(`Missing ${name} animation IDLE.AN3.`);
  return { name, buffer: await ghg.arrayBuffer(), config, animations, animationSources, authoredDirectory: directory, inheritedFrom: current === name ? null : current };
}
